/**
 * Left column of the pharmacist order page: who the order is for (icons per field, so it scans
 * quickly) and, once claimed, the order's history — when the prescription was written and when
 * the patient ordered, then each step taken since.
 */

import type { ReactNode } from "react";

import type { PharmacistOrder } from "../../api/orders";
import { OrderStatusBadge } from "../../components/OrderStatusBadge";
import { doctorName } from "../../lib/doctorName";
import { ScheduleBadge } from "../patient/Prescriptions";

const DATE: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
const DATE_TIME: Intl.DateTimeFormatOptions = { ...DATE, hour: "numeric", minute: "2-digit" };
const fmt = (iso: string, opts: Intl.DateTimeFormatOptions) => new Date(iso).toLocaleString("en-IN", opts);

export function PatientCard({ order }: { order: PharmacistOrder }) {
  const addr = order.delivery_address_snapshot as Record<string, string | undefined>;
  return (
    <section className="rounded-xl border bg-white p-5 shadow-sm">
      {/* The reference staff and patients quote to each other on the phone. */}
      <div className="font-mono text-base font-semibold">{order.order_id}</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <OrderStatusBadge code={order.status_code} label={order.status_label} />
        <ScheduleBadge cls={order.schedule_class} />
      </div>
      {(order.warnings.is_zero_purchase || order.warnings.is_first_order || order.warnings.address_changed) && (
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {order.warnings.is_zero_purchase && <Warn>Zero-purchase account</Warn>}
          {order.warnings.is_first_order && <Warn>First order</Warn>}
          {order.warnings.address_changed && <Warn>Ad-hoc address</Warn>}
        </div>
      )}

      <dl className="mt-4 space-y-3 text-sm">
        <Field icon={<PersonIcon />} label="Patient">
          <span className="font-medium text-ink">{order.patient_name}</span>
        </Field>
        {order.patient_phone && (
          <Field icon={<PhoneIcon />} label="Phone">
            <a href={`tel:${order.patient_phone}`} className="text-brand hover:underline">
              {order.patient_phone}
            </a>
          </Field>
        )}
        <Field icon={<PinIcon />} label="Deliver to">
          <span className="block text-ink">
            {addr.name}
            {addr.phone ? ` · ${addr.phone}` : ""}
          </span>
          <span className="block text-ink-soft">
            {addr.line1}
            {addr.line2 ? `, ${addr.line2}` : ""}
            {addr.landmark ? ` (near ${addr.landmark})` : ""}, {addr.city}, {addr.state_code} {addr.pin_code}
          </span>
        </Field>
        {order.placement_note && (
          <Field icon={<NoteIcon />} label="Patient's note">
            <span className="whitespace-pre-line text-ink">{order.placement_note}</span>
          </Field>
        )}
      </dl>
    </section>
  );
}

/** The order's history, oldest first. Shown once the order is claimed. */
export function OrderTimeline({ order }: { order: PharmacistOrder }) {
  const rx = order.prescription;
  const events: { when: string; title: string; detail?: string }[] = [];
  if (rx) {
    events.push(
      rx.source === "doctor"
        ? {
            // Written in the app: the doctor's own time of issue.
            when: fmt(rx.issued_at, DATE_TIME),
            title: "Prescription issued",
            detail: `${rx.ref}${rx.doctor_name ? ` · by ${doctorName(rx.doctor_name)}` : ""}`,
          }
        : {
            // Digitised from the patient's scanned paper copy: the date only.
            when: fmt(rx.issued_at, DATE),
            title: "Prescription (from uploaded copy)",
            detail: `${rx.ref} · digitised from the scan${rx.doctor_name ? `, under Dr. ${rx.doctor_name}` : ""}`,
          },
    );
  }
  events.push({ when: fmt(order.created_at, DATE_TIME), title: "Order placed by the patient" });
  if (order.claimed_at) events.push({ when: fmt(order.claimed_at, DATE_TIME), title: "Claimed" });
  if (order.address_confirmed_at) events.push({ when: fmt(order.address_confirmed_at, DATE_TIME), title: "Address confirmed" });
  if (order.pricing_published_at) events.push({ when: fmt(order.pricing_published_at, DATE_TIME), title: "Price published" });
  if (order.paid_at) events.push({ when: fmt(order.paid_at, DATE_TIME), title: "Paid" });
  if (order.cancelled_at) events.push({ when: fmt(order.cancelled_at, DATE_TIME), title: "Cancelled" });

  return (
    <section className="rounded-xl border bg-white p-5 shadow-sm" aria-label="Order history">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
        <span className="h-4 w-4 text-ink-muted"><ClockIcon /></span>
        Order history
      </h3>
      <ol className="mt-3 space-y-3 border-l border-line pl-4">
        {events.map((e) => (
          <li key={e.title} className="relative text-sm">
            <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-brand" aria-hidden />
            <p className="font-medium text-ink">{e.title}</p>
            <p className="text-xs text-ink-soft">{e.when}</p>
            {e.detail && <p className="text-xs text-ink-muted">{e.detail}</p>}
          </li>
        ))}
      </ol>
    </section>
  );
}

function Field({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" aria-hidden>{icon}</span>
      <div className="min-w-0">
        <dt className="text-xs text-ink-muted">{label}</dt>
        <dd>{children}</dd>
      </div>
    </div>
  );
}

function Warn({ children }: { children: ReactNode }) {
  return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">{children}</span>;
}

/* --- inline icons (stroke=currentColor) --- */

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-full w-full">
      {children}
    </svg>
  );
}
function PersonIcon() {
  return (
    <Svg>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </Svg>
  );
}
function PhoneIcon() {
  return (
    <Svg>
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
    </Svg>
  );
}
function PinIcon() {
  return (
    <Svg>
      <path d="M12 21s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12Z" />
      <circle cx="12" cy="9" r="2.5" />
    </Svg>
  );
}
function NoteIcon() {
  return (
    <Svg>
      <path d="M4 5h16v11H8l-4 3V5Z" />
      <path d="M8 9h8M8 12h5" />
    </Svg>
  );
}
function ClockIcon() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Svg>
  );
}
